import { useState } from "react";
import useSWR from "swr";
import { fetcher } from "../../../../services/fetcher";
import { Button, Image, Paper, Box, Center, Text, Badge, Drawer } from "@mantine/core";
import { useToggle } from "@mantine/hooks";
import { useForm } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import DataTable from "../../../../common/components/DataTable/DataTable";
import {
  errorNotifications,
  successNotifications,
} from "../../../../utils/handleNotifications";
import MainLoader from "../../../../common/components/Loader/Loader";

const assignPlan = "issuance/assign-plan/";
const coversEndpoint = "issuance/covers-and-customize-covers/";

const AssignCoverOperator = () => {
  const outletContext = useOutletContext();
  const operator = outletContext ? outletContext.operator : null;
  const [currentSelection, setCurrentSelection] = useState([]);
  const [removalSelection, setRemovalSelection] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [coverFilters, setCoverFilters] = useState({});
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [activeCoverId, setActiveCoverId] = useState(null);

  const [value, toggle] = useToggle(["add", "view"]);
  const filterForm = useForm();

  const operatorEmail = operator ? operator.operator.email : null;
  const assignPlanUrl = `${assignPlan}?user_email=${operatorEmail}`;

  const {
    data: plans,
    error: plansError,
    isLoading: isPlansLoading,
    mutate: mutatePlans,
  } = useSWR(assignPlanUrl, fetcher);

  const {
    data: covers,
    error: coversError,
    isLoading: isCoversLoading,
  } = useSWR(plans ? coversEndpoint : null, fetcher, {
    dedupingInterval: 9000,
  });

  const { data: coverDetails, error: coverDetailsError, isLoading: isCoverLoading } = useSWR(
    activeCoverId ? `cover-api/customize-covers/${activeCoverId}/` : null,
    fetcher,
    { revalidateOnFocus: false, dedupingInterval: 15000 }
  );

  const brochureName = coverDetails?.brochure_name || coverDetails?.brochure || "";
  const isDev = import.meta.env.MODE === "development";
  const baseURL = isDev ? import.meta.env.VITE_API_ENDPOINT : "";
  const isAbsolute = /^https?:\/\//i.test(brochureName);
  const resolvedUrl = brochureName ? (isAbsolute ? brochureName : `${baseURL}${brochureName}`) : "";

  const headerGradientStyle = {
    color: "transparent",
    backgroundImage: "linear-gradient(to left, #0053A8, #033F7C)",
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    display: "inline-block",
  };

  const handleRowClick = (row) => {
    if (!row?.id) return;
    setActiveCoverId(row.id);
    setDrawerOpened(true);
  };

  const plansConfig = {
    itemsPerPage: 8,
    selectionCheckbox: true,
    showFilterButton: true,
    selectionColumnWidth: 60,
    selectionCheckboxSize: 20,
    selectionCheckboxRadius: 4,
    selectionCheckboxStyles: {
      input: {
        width: 20,
        height: 20,
        borderRadius: 4,
        borderColor: "#AEB6BE",
      },
    },
    selectionHeaderStyle: {
      height: 54,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 24,
      paddingRight: 16,
    },
    selectionCellStyle: {
      height: 54,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 24,
      paddingRight: 16,
    },
    tableStyles: {
      th: { height: 54, padding: 0, borderBottom: "1px solid #D6E4EF", verticalAlign: "middle" },
      td: { height: 54, padding: 0, verticalAlign: "middle" },
    },
    horizontalSpacing: 0,
    verticalSpacing: 0,
    highlightOnHover: false,
    toolbarLeftGap: 12,
    searchInputWidth: 248,
    searchInputHeight: 48,
    searchIconSize: 16,
    searchInputStyles: {
      input: {
        height: 48,
        minHeight: 48,
        backgroundColor: "#FFFFFF",
        borderRadius: 8,
        border: "1px solid rgba(108, 148, 180, 0.15)",
        paddingLeft: 40,
        paddingRight: 16,
        color: "#6C94B4",
        fontSize: 14,
        fontWeight: 400,
      },
    },
    selectionActionButtonProps: { h: 48, px: 24, radius: 8, style: { minWidth: 168 } },
    fields: [
      {
        key: "title",
        label: "Plan Name",
        sortable: false,
        width: 282,
        headerControlStyle: { width: "100%", padding: 0 },
        labelStyle: { width: 282, fontSize: 14, fontWeight: 500, lineHeight: "18px", ...headerGradientStyle },
        headerStyle: { height: 54, padding: 0 },
        cellStyle: { height: 54, padding: 0 },
        render: (row) => (
          <Text c="#092C4C" fw={500} fz={14} lh="18px" w={282} style={{ whiteSpace: "normal" }}>
            {row?.title}
          </Text>
        ),
      },
      {
        key: "cover_scope",
        label: "Scope",
        sortable: false,
        width: 98,
        headerControlStyle: { width: "100%", padding: 0 },
        labelStyle: { width: 98, fontSize: 14, fontWeight: 500, lineHeight: "18px", ...headerGradientStyle },
        headerStyle: { height: 54, padding: 0 },
        cellStyle: { height: 54, padding: 0 },
        render: (row) => <Text c="#6C94B4" fw={500} fz={14} lh="18px" w={98}>{row?.cover_scope}</Text>,
      },
      {
        key: "commission_display",
        label: "Commission",
        sortable: false,
        width: 80,
        headerControlStyle: { width: "100%", padding: 0 },
        labelStyle: { width: 80, fontSize: 14, fontWeight: 500, lineHeight: "18px", ...headerGradientStyle },
        headerStyle: { height: 54, padding: 0 },
        cellStyle: { height: 54, padding: 0 },
        render: (row) => (
          <Text c="#6C94B4" fw={500} fz={14} lh="18px" w={80}>
            {row?.commission_value ? `₹${row.commission_value}` : "₹68 ~10%"}
          </Text>
        ),
      },
      {
        key: "currency",
        label: "Currency",
        sortable: false,
        width: 70,
        headerControlStyle: { width: "100%", padding: 0 },
        labelStyle: { width: 70, fontSize: 14, fontWeight: 500, lineHeight: "18px", ...headerGradientStyle },
        headerStyle: { height: 54, padding: 0 },
        cellStyle: { height: 54, padding: 0 },
        render: (row) => <Text c="#6C94B4" fw={500} fz={14} lh="18px" w={70}>{row?.currency}</Text>,
      },
      {
        key: "price",
        label: "Price",
        sortable: false,
        width: 54,
        headerControlStyle: { width: "100%", padding: 0 },
        labelStyle: { width: 54, fontSize: 14, fontWeight: 500, lineHeight: "18px", ...headerGradientStyle },
        headerStyle: { height: 54, padding: 0 },
        cellStyle: { height: 54, padding: 0 },
        render: (row) => <Text c="#6C94B4" fw={500} fz={14} lh="18px" w={54}>{row?.price}</Text>,
      },
      {
        key: "status",
        label: "Status",
        sortable: false,
        width: 70,
        headerControlStyle: { width: "100%", padding: 0 },
        labelStyle: { width: 70, fontSize: 14, fontWeight: 500, lineHeight: "18px", ...headerGradientStyle },
        headerStyle: { height: 54, padding: 0 },
        cellStyle: { height: 54, padding: 0 },
        render: () => (
          <Badge radius={4} variant="filled" tt="none" styles={{ root: { width: 54, height: 18, background: "#3AC65E" }, label: { fontSize: 10, fontWeight: 600, color: "#FFFFFF" } }}>
            Active
          </Badge>
        ),
      },
    ],
  };

  const handleSelection = (newSelection) => {
    setCurrentSelection(newSelection);
  };

  const handleRemovalSelection = (newSelection) => {
    setRemovalSelection(newSelection);
  };

  const handleAssignCover = async () => {
    if (currentSelection.length === 0) {
      return successNotifications("Please select covers");
    }

    try {
      const customizeCoverIds = [];
      currentSelection.forEach((id) => {
        if (covers.customize_covers.some((customizeCover) => customizeCover.id === id)) {
          customizeCoverIds.push(id);
        }
      });

      const currentCustomize = Array.isArray(plans.customize_covers)
        ? plans.customize_covers.map((customize) => customize.id)
        : [];

      const payload = {
        user_email: operator.operator.email,
        customize_cover_ids: [...currentCustomize, ...customizeCoverIds],
      };

      await fetcher("issuance/assign-plan/", "POST", payload);
      await mutatePlans();
      toggle();
      successNotifications("Success, Covers assigned");
    } catch (error) {
      errorNotifications(error.response?.data || "An error occurred", error);
    }
  };

  const handleRemoveCover = async () => {
    if (removalSelection.length === 0) {
      return successNotifications("Please select covers to remove");
    }

    try {
      const currentCustomize = Array.isArray(plans.customize_covers)
        ? plans.customize_covers.map((customize) => customize.id)
        : [];

      const remainingIds = currentCustomize.filter((id) => !removalSelection.includes(id));

      const payload = {
        user_email: operator.operator.email,
        customize_cover_ids: remainingIds,
      };

      await fetcher("issuance/assign-plan/", "POST", payload);
      await mutatePlans();
      setRemovalSelection([]);
      successNotifications("Success, Plan removed");
    } catch (error) {
      errorNotifications(error.response?.data || "An error occurred", error);
    }
  };

  if (isPlansLoading) {
    return <MainLoader />;
  }

  if (plansError) {
    return <p>Failed to load plans</p>;
  }

  const assignedCovers = Array.isArray(plans?.customize_covers) ? plans.customize_covers : [];
  const combinedData = covers
    ? covers.customize_covers.filter((cover) => !assignedCovers.some((assignedCover) => assignedCover.id === cover.id))
    : [];
  const isPlansEmpty = assignedCovers.length === 0;
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const getUniqueOptions = (items, key) => {
    const values = [...new Set((items || []).map((item) => item?.[key]).filter(Boolean))];
    return values.map((item) => ({ value: String(item), label: String(item) }));
  };
  const allCoverRows = [...assignedCovers, ...combinedData];
  const coverFilterOptions = [
    {
      label: "Scope",
      value: "cover_scope",
      type: "checkbox",
      options: getUniqueOptions(allCoverRows, "cover_scope"),
    },
    {
      label: "Currency",
      value: "currency",
      type: "checkbox",
      options: getUniqueOptions(allCoverRows, "currency"),
    },
  ];
  const matchesSelectedFilters = (cover) => {
    const selectedScope = Array.isArray(coverFilters.cover_scope) ? coverFilters.cover_scope : [];
    const selectedCurrency = Array.isArray(coverFilters.currency) ? coverFilters.currency : [];
    const scope = String(cover?.cover_scope || "");
    const currency = String(cover?.currency || "");
    const matchesScope = selectedScope.length === 0 || selectedScope.includes(scope);
    const matchesCurrency = selectedCurrency.length === 0 || selectedCurrency.includes(currency);
    return matchesScope && matchesCurrency;
  };
  const filterCovers = (items) => {
    return items.filter((cover) => {
      if (!matchesSelectedFilters(cover)) return false;
      if (!normalizedQuery) return true;
      const title = String(cover?.title || "").toLowerCase();
      const scope = String(cover?.cover_scope || "").toLowerCase();
      const commission = String(cover?.commission_value || "").toLowerCase();
      const currency = String(cover?.currency || "").toLowerCase();
      const price = String(cover?.price || "").toLowerCase();
      return (
        title.includes(normalizedQuery) ||
        scope.includes(normalizedQuery) ||
        commission.includes(normalizedQuery) ||
        currency.includes(normalizedQuery) ||
        price.includes(normalizedQuery)
      );
    });
  };
  const handleCoverFilterSubmit = (formData) => {
    setCoverFilters(formData || {});
  };
  const filteredAssignedCovers = filterCovers(assignedCovers);
  const filteredCombinedData = filterCovers(coversError ? [] : combinedData);

  const EmptyComponent = () => (
    <Paper shadow="xl" style={{ backgroundColor: "#fff" }}>
      <Box style={{ textAlign: "center", padding: "20vh" }}>
        <Center>
          <Image src="/images/add-icon.png" width={80} height={114} alt="avatar" />
        </Center>
        <p>No covers available.</p>
          <Button variant="outline" onClick={() => toggle()}>
            {value === "add" ? "+ Assign Covers" : "View Covers"}
          </Button>
      </Box>
    </Paper>
  );

  return (
    <>
      <Drawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        position="right"
        size="50%"
        title="Brochure"
        styles={{
          body: {
            flex: 1,
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
          content: { display: "flex", flexDirection: "column" },
        }}
      >
        {isCoverLoading ? (
          <Box p="md">
            <Text size="sm">Loading brochure...</Text>
          </Box>
        ) : resolvedUrl ? (
          <iframe
            title="Cover brochure"
            src={resolvedUrl}
            style={{
              border: 0,
              width: "100%",
              height: "100%",
              maxWidth: 900,
              maxHeight: "100%",
            }}
          />
        ) : coverDetailsError ? (
          <Box p="md">
            <Text size="sm">Failed to load brochure.</Text>
          </Box>
        ) : (
          <Box p="md">
            <Text size="sm">No brochure available.</Text>
          </Box>
        )}
      </Drawer>
      {value === "add" ? (
        isPlansEmpty ? (
          <EmptyComponent />
        ) : (
          <>
            <DataTable
              config={{
                ...plansConfig,
                onRowClick: handleRowClick,
                selectionActions: [
                  { label: "Assign New Plan", handler: () => toggle() },
                  { label: "Remove Plan", handler: handleRemoveCover },
                ],
              }}
              data={filteredAssignedCovers}
              onSelectionChange={handleRemovalSelection}
              onSearch={(event) => setSearchQuery(event.target.value)}
              form={filterForm}
              filterOptions={coverFilterOptions}
              onSubmit={handleCoverFilterSubmit}
            />
          </>
        )
      ) : (
        <>
          <DataTable
            config={{
              ...plansConfig,
              onRowClick: handleRowClick,
              selectionActions: [{ label: "Save", handler: handleAssignCover }],
            }}
            data={filteredCombinedData}
            onSelectionChange={handleSelection}
            onSearch={(event) => setSearchQuery(event.target.value)}
            form={filterForm}
            filterOptions={coverFilterOptions}
            onSubmit={handleCoverFilterSubmit}
            loading={isCoversLoading}
          />
        </>
      )}
    </>
  );
};

export default AssignCoverOperator;